import React, { useContext, useEffect, useState } from "react";
import { Panel } from "../../../common/components/panel/panel";
import { PayFileColumnsDefinitions } from "./ColumnDefinition/PayFileColumnsDefinitions";
import CustomerProfileContext from "../../../common/components/context/StandAloneContext";
import { TableV } from "../../../common/components/table/tablev2";
import { PayFilePaymentsColumnsDefinitions } from "./ColumnDefinition/PayFilePaymentsColumnsDefinitions";
import { PayFileRemittanceColumnsDefinitions } from "./ColumnDefinition/PayFileRemittanceColumnsDefinitions";
import { PayFileGarnishmentColumnsDefinitions } from "./ColumnDefinition/PayFileGarnishmentColumnsDefinitions";
import { GetPayrunImportById, GetPaymentImportDetailById, GetTaxesImportDetailById, GetGarnishmentImportDetailById, GetValidationErrorsByImportId } from "../../../services/ImportDetailService";
import { ImportPayment } from "../../../models/ImportFile";
import { ErrorColumnsDefinitions } from "../Error/ErrorColumnsDefinitions";
import { StandAloneActions, Status } from "../../../models/StandAloneData";
import "./Pay.scss";

export const Pay: React.FC<{ rowsPerPage?: any}> = ({
    rowsPerPage = 25,
  }) => {

    const { state, dispatch } = useContext(CustomerProfileContext);
    const [errors, setErrors] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(true);
    const [loadingRemittance, setLoadingRemittance] = useState(true);
    const [loadingGarnishment, setLoadingGarnishment] = useState(true);
    const [loadingPayRun, setLoadingPayRun] = useState(true);
    const [loadingError, setLoadingError] = useState(true);

    const [showPayment, setShowPayment] = useState(false);
    const [showRemittance, setShowRemittance] = useState(false);
    const [showGarnishment, setShowGarnishment] = useState(false);
    const [payRunData, setPayRunData] = useState([]);
    const [paymentsData, setPaymentsData] = useState<ImportPayment[]>();
    const [taxesData, setTaxesData] = useState<any[]>();
    const [errorData, setErrorData] = useState<any[]>([]);
    const [garnishmentData, setGarnishmentData] = useState<any[]>();

    async function GetPayRunFileData() {
        try {
            const response =  await GetPayrunImportById(state.importId);
            if(response) {
                const data = {
                    fileName: state?.fileName,
                    payDate: response?.payDate,
                    periodEndDate: response?.periodEndDate,
                    payment: response?.payments.toString(),
                    remuneration: response?.remuneration.toString(),
                    garnishment: response?.garnishment.toString(),
                    total: response?.total
                };
                dispatch({
                    type: StandAloneActions.SET_IMPORT_DETAIL,
                    payload: { 
                        ...state,
                        importDetailId: response?.id,
                    },
                });
                setPayRunData([data]);
            }            
            setLoadingPayRun(false);
        } catch (error) {
            return new Error(error)
        }
    };

    async function GetPaymentImportDetail() {
        try {
            const resp = await GetPaymentImportDetailById(state.importDetailId);
            setPaymentsData(resp);
            setLoadingPayment(false);
        } catch (error) {
            return new Error(error)
        }
    };

    async function GetTaxesImportDetails() {
        try {
            const resp = await GetTaxesImportDetailById(state.importDetailId, state.taxTableIndex, rowsPerPage);
            const output = Object.groupBy(resp, groupData);
            if(output?.data?.length) {
                setTaxesData(output.data);
                setLoadingRemittance(false);
            }
        } catch (error) {
            return new Error(error)
        }    
    };

    async function GetGarnishmentImportDetails() {
        try {
            const resp = await GetGarnishmentImportDetailById(state.importDetailId, state.taxTableIndex, rowsPerPage);
            setGarnishmentData(resp);
            setLoadingGarnishment(false);
        } catch (error) {
            return new Error(error)
        }    
    };

    const groupData = ({taxNumber}) => {
        if(taxNumber != null) {
            return 'data';
        }
    };

    const calculateTaxesTotal = (): string => {
        let total = 0;
        if(taxesData && taxesData.length > 0) {
            taxesData.forEach(x => {
                total += x.amount;
            })
        }
        return total.toFixed(2);
    }

    async function GetValidationErrors() {
        try {
            const resp = await GetValidationErrorsByImportId(state.importId);
            if(resp && resp.length > 0 ) {
                setErrorData(resp);
            }
            setLoadingError(false);
        } catch (error) {
            return new Error(error)
        }  
    }

    useEffect(() => {
        if(state.status === Status.Error || state.status === Status.TransmissionError) {
            GetValidationErrors();
            setErrors(true);
        } 
        else {
            GetPayRunFileData(); 
        }

    }, [state.status]);

    useEffect(() => {
        if(showPayment) {
            GetPaymentImportDetail();
        } 

        if(showRemittance) {
            GetTaxesImportDetails();
        }
        if(showGarnishment) {
            GetGarnishmentImportDetails();
        }

    }, [showPayment, showRemittance, showGarnishment]);

    useEffect(() => {
        if (state.taxTableIndexChange !== null) {
            GetTaxesImportDetails();
        }
    }, [state.taxTableIndexChange]);

    const goToNextPage = () => {
        dispatch({
            type: StandAloneActions.SET_TAX_PAGE,
            payload: { 
                ...state,
                pageIdx: state.taxTableIndex + 1
            },
        });
    };

    return (
        <Panel fullWidth={true}>
                {!errors && 
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui-pay"} 
                            isLoading={loadingPayRun}
                            columns={PayFileColumnsDefinitions(setShowPayment, setShowRemittance, setShowGarnishment)} 
                            data={payRunData} 
                            dataKey={"Canada"}
                            dataLength={null} 
                            description={"Pay File Data"} 
                            noDataText={"No Data Available"}
                            addItemText={""}
                            tableHeight="30vh"
                            rowsPerPage={rowsPerPage}
                    />
                </Panel.Block> 
                }             
                {showPayment && 
                    <Panel.Block>
                        <TableV 
                            testId={"canada-standalone-ui-payment"}
                            isLoading={loadingPayment} 
                            columns={PayFilePaymentsColumnsDefinitions()} 
                            data={paymentsData} 
                            dataKey={"Canada"}
                            dataLength={null}  
                            description={"Payments"} 
                            noDataText={"No Data Available"}
                            addItemText={""}
                            tableHeight="60vh"
                            rowsPerPage={rowsPerPage}
                        />
                    </Panel.Block>
                }
                {showRemittance &&
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui-remittance"}
                            isLoading={loadingRemittance} 
                            columns={PayFileRemittanceColumnsDefinitions()} 
                            data={taxesData} 
                            dataKey={"Canada"}
                            dataLength={null}  
                            description={"Remittance"} 
                            noDataText={"No Data Available"}
                            addItemText={""}
                            tableHeight="60vh"
                            rowsPerPage={rowsPerPage}
                            pageIndex={state.taxTableIndex}
                            getNextPageDetailsFromAPI={goToNextPage}
                        />
                    </Panel.Block>
                }
                {showRemittance && 
                    <Panel.Block>
                        <section>
                            <p>Total Remittances Amount:  ${calculateTaxesTotal()}</p>
                        </section>
                    </Panel.Block>
                }
                {showGarnishment &&
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui-garnishment"}
                            isLoading={loadingGarnishment} 
                            columns={PayFileGarnishmentColumnsDefinitions()} 
                            data={garnishmentData} 
                            dataKey={"Canada"}
                            dataLength={null}  
                            description={"Garnishment"} 
                            noDataText={"No Data Available"}
                            addItemText={""}
                            tableHeight="60vh"
                            rowsPerPage={rowsPerPage}
                            pageIndex={state.taxTableIndex}
                            getNextPageDetailsFromAPI={goToNextPage}
                        />
                    </Panel.Block>
                }
                {errors && 
                    <Panel.Block>
                        <TableV
                            testId={"canada-standalone-ui-pay-error"}
                            isLoading={loadingError} 
                            columns={ErrorColumnsDefinitions()} 
                            data={errorData} 
                            dataKey={"Canada"}
                            dataLength={null}  
                            description={"Pay Error Data"} 
                            noDataText={""}
                            addItemText={""}
                            tableHeight="83vh"
                            rowsPerPage={rowsPerPage}
                        />
                    </Panel.Block>
                }
            </Panel>
    );
}